import { feedback, feedbackV2 } from '@ecosystem/newron-design-system';
import React, { useCallback, useState } from 'react';

import { FilterPermissions, SavedFilter } from '../types';
import { EmptyFilters } from '../EmptyFilters';
import { canShareFilter } from '../canShareFilter';
import { FilterCard, FilterCardStatus } from '../FilterCard';
import { ApplyAction, DeleteAction, ShareAction } from '../actions';
import { DeleteFilterModal, GetUsersFunction, ShareFilterModal } from '../modals';
import { deleteFilter } from '../deleteFilter';
import { mapUsersToPermissions } from '../utils';
import { ShareUser } from '../../sharing/types';
import { useFilterTrayContext } from './context';
import { useLogging } from '../../logging';

const { DotsLoader } = feedback;
const { Alert } = feedbackV2;

const FAILED_TO_LOAD_ALERT =
  'There has been an error loading saved filters. Please refresh your browser to try again. If this issue persists, please contact your GfK representative.';
const FILTER_SHARED_ERROR_MESSAGE = 'Failed to save changes for sharing the filter.';
const FILTER_SHARED_MESSAGE = (filterName: string): string => `Changes for sharing of filter "${filterName}" saved.`;

export type SavedFiltersProps = {
  savedFilters: SavedFilter[];
  loading?: boolean;
  savedFiltersError?: boolean;
  getUsers: GetUsersFunction;
  onSavedFilterApplied: (filter: SavedFilter) => void;
  onSavedFilterDeleted: (filterId: string) => void;
  onSavedFilterShared: (filterId: string, updatedPermissions: FilterPermissions[]) => void;
};

export const SavedFilters = ({
  savedFilters,
  getUsers,
  loading,
  savedFiltersError,
  onSavedFilterApplied,
  onSavedFilterDeleted,
  onSavedFilterShared,
}: SavedFiltersProps): JSX.Element => {
  const { setToast } = useFilterTrayContext();
  const { error: logError } = useLogging();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<SavedFilter>();

  const unavailableFilters = savedFilters?.length > 0 ? savedFilters.filter(filter => filter.isUnavailable) : [];
  const availableFilters = savedFilters?.length > 0 ? savedFilters.filter(filter => !filter.isUnavailable) : [];

  const handleDelete = (filterId: string): void => {
    setSelectedFilter(savedFilters.find(filter => filter.id === filterId));
    setDeleteModalOpen(true);
  };

  // Actually deletes the selected filter in the backend.
  // ATTENTION: Sharing a filter is done in the ShareFilterModal.
  const handleDeleteSelectedFilter = async () => {
    await deleteFilter(selectedFilter);
    setDeleteModalOpen(false);
    setSelectedFilter(undefined);
    onSavedFilterDeleted(selectedFilter.id);
  };

  const handleShare = (filterId: string): void => {
    setSelectedFilter(savedFilters.find(filter => filter.id === filterId));
    setShareModalOpen(true);
  };

  const handleShareSuccess = useCallback(
    (users: ShareUser[]) => {
      const updatedPermissions = mapUsersToPermissions(users);
      onSavedFilterShared(selectedFilter.id, updatedPermissions);
      setToast({
        type: 'success',
        message: FILTER_SHARED_MESSAGE(selectedFilter.name),
      });
    },
    [selectedFilter, onSavedFilterShared, setToast]
  );

  const handleShareError = useCallback(
    (error: Error) => {
      setToast({
        type: 'error',
        message: FILTER_SHARED_ERROR_MESSAGE,
      });
      logError(error, { message: 'Failed to share filter' });
    },
    [logError, setToast]
  );

  const handleCloseModals = useCallback(() => {
    setDeleteModalOpen(false);
    setShareModalOpen(false);
    setSelectedFilter(undefined);
  }, []);

  const handleApply = useCallback(
    (filter: SavedFilter) => {
      setSelectedFilter(filter);
      onSavedFilterApplied(filter);
    },
    [onSavedFilterApplied]
  );

  return (
    <>
      {loading && <DotsLoader testId="dots-loader" />}
      {!!savedFiltersError && <Alert title={FAILED_TO_LOAD_ALERT} type="error" />}
      {!savedFiltersError && !loading && savedFilters?.length === 0 && <EmptyFilters />}
      {availableFilters.map(filter => (
        <FilterCardWrapper
          key={filter.id}
          filter={filter}
          onApply={() => handleApply(filter)}
          onDelete={() => handleDelete(filter.id)}
          onShare={() => handleShare(filter.id)}
        />
      ))}
      {unavailableFilters.length > 0 && <UnavailableHeader />}
      {unavailableFilters.map(filter => (
        <FilterCardWrapper
          key={filter.id}
          filter={filter}
          onDelete={() => handleDelete(filter.id)}
          onShare={() => handleShare(filter.id)}
        />
      ))}
      {deleteModalOpen && (
        <DeleteFilterModal
          isOpen={true}
          itemToDelete={selectedFilter}
          getUsers={getUsers}
          onClose={handleCloseModals}
          onDelete={handleDeleteSelectedFilter}
        />
      )}
      {shareModalOpen && (
        <ShareFilterModal
          isOpen={true}
          itemToShare={selectedFilter}
          onClose={handleCloseModals}
          onShareError={handleShareError}
          getUsers={getUsers}
          onSuccess={handleShareSuccess}
        />
      )}
    </>
  );
};

const UnavailableHeader = (): JSX.Element => {
  return (
    <>
      <hr className="mt-4" />
      <div className="py-4">
        <h4 className="font-normal">Unavailable</h4>
        <p className="text-xs text-gray-100" style={{ color: '#9499A3' }}>
          These saved filters can not be applied in the selected market
        </p>
      </div>
      <hr className="mb-4" />
    </>
  );
};

type FilterCardWrapperProps = {
  filter: SavedFilter;
  onApply?: () => void;
  onDelete?: () => void;
  onShare?: () => void;
};

const getStatus = (filter: SavedFilter): FilterCardStatus => {
  if (filter.isNew) {
    return 'new';
  } else if (filter.isApplied) {
    return 'applied';
  }

  return undefined;
};

const FilterCardWrapper = ({ filter, onApply, onDelete, onShare }: FilterCardWrapperProps): JSX.Element => {
  const getTrackingTags = elementName => () => ({
    elementName,
    elementType: 'Button',
    elementCategory: 'Saved Filters',
  });

  const filterCriteria = filter.criteria;
  const canShare = canShareFilter(filter);

  return (
    // once FilterCard is not used by other propositions anymore, we could move the margin there
    <div className="mb-2">
      <FilterCard filter={filter} status={getStatus(filter)}>
        <FilterCard.Content>
          {filterCriteria.map(entity => {
            const value = Array.isArray(entity.value) ? entity.value.join(', ') : entity.value;
            return (
              <div key={entity.name + '-' + value} className={`${filter.isUnavailable && 'text-inactive'}`}>
                <span className="text-xs">{`${entity.name}: `}</span>
                <span className="font-bold text-xs">{value}</span>
              </div>
            );
          })}
        </FilterCard.Content>
        <FilterCard.Footer>
          <FilterCard.Footer.Actions>
            <ApplyAction
              testId="apply-action"
              onClick={onApply}
              disabled={filter.isUnavailable}
              getTrackingTags={getTrackingTags('Apply Saved Filter')}
            />
            <DeleteAction
              testId="delete-action"
              onClick={onDelete}
              getTrackingTags={getTrackingTags('Delete Saved Filter')}
            />
            <ShareAction
              testId="share-action"
              onClick={onShare}
              disabled={!canShare}
              getTrackingTags={getTrackingTags('Share Saved Filter')}
            />
          </FilterCard.Footer.Actions>
        </FilterCard.Footer>
      </FilterCard>
    </div>
  );
};
